<template>
  <div class="mb-2" data-cy="installment-row-wrapper">
    <div class="d-flex align-items-center gap-2">
      <input
        :value="installment.label"
        type="text"
        class="form-control"
        placeholder="Label"
        :disabled="disabled"
        data-cy="installment-label"
        @input="onLabelInput"
      />

      <div
        class="input-group spb-row__value"
        style="width: 140px; flex: 0 0 140px"
      >
        <span v-if="splitMode === 'amount'" class="input-group-text">$</span>
        <input
          :value="displayValue"
          type="number"
          min="0"
          step="0.01"
          class="form-control"
          :class="{ 'is-invalid': Boolean(error) }"
          :disabled="disabled"
          data-cy="installment-value"
          @focus="onValueFocus"
          @blur="onValueBlur"
          @input="onValueInput"
        />
        <span v-if="splitMode === 'percentage'" class="input-group-text"
          >%</span
        >
      </div>

      <span
        class="text-muted small"
        style="width: 90px; flex: 0 0 90px"
        data-cy="calculated-opposite"
      >
        {{ oppositeDisplay }}
      </span>

      <input
        :value="installment.due_date ?? ''"
        type="date"
        class="form-control"
        style="width: 160px; flex: 0 0 160px"
        :disabled="disabled"
        :min="minDate"
        data-cy="installment-due-date"
        @input="onDueDateInput"
      />

      <button
        v-if="removable"
        type="button"
        class="btn btn-link text-danger p-0"
        :disabled="disabled"
        data-cy="remove-installment"
        :aria-label="`Remove ${installment.label}`"
        @click="$emit('remove')"
      >
        <i class="fas fa-trash-alt" />
      </button>
    </div>

    <div
      v-if="error"
      class="text-danger small mt-1"
      data-cy="installment-error"
    >
      {{ error }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";

import { dollars } from "@/mixins";

type SplitMode = "percentage" | "amount";

interface Installment {
  id?: number;
  label: string;
  label_overridden?: boolean;
  value: number;
  calculated_opposite_value: number;
  position: number;
  due_date?: string | null;
  _destroy?: boolean;
}

const props = defineProps<{
  installment: Installment;
  splitMode: SplitMode;
  removable: boolean;
  error?: string | null;
  disabled?: boolean;
  minDate?: string;
}>();

const emit = defineEmits<{
  remove: [];
  "update:label": [value: string];
  "update:value": [value: number];
  "update:dueDate": [value: string | null];
}>();

const isValueFocused = ref(false);
const draftValue = ref("");

const displayValue = computed(() => {
  if (isValueFocused.value) return draftValue.value;
  if (props.splitMode === "amount") {
    return (props.installment.value / 100).toFixed(2);
  }
  return String(props.installment.value);
});

const oppositeDisplay = computed(() => {
  const v = props.installment.calculated_opposite_value;
  if (props.splitMode === "percentage") return dollars(v);
  return `${Number(v).toFixed(2)}%`;
});

function onLabelInput(event: Event): void {
  const target = event.target as HTMLInputElement;
  emit("update:label", target.value);
}

function onValueFocus(event: FocusEvent): void {
  const target = event.target as HTMLInputElement;
  draftValue.value = target.value;
  isValueFocused.value = true;
}

function onValueBlur(): void {
  isValueFocused.value = false;
  draftValue.value = "";
}

function onValueInput(event: Event): void {
  const raw = (event.target as HTMLInputElement).value;
  draftValue.value = raw;
  const parsed = Number(raw);
  if (Number.isNaN(parsed)) return;
  const stored =
    props.splitMode === "amount" ? Math.round(parsed * 100) : parsed;
  emit("update:value", stored);
}

function onDueDateInput(event: Event): void {
  const value = (event.target as HTMLInputElement).value;
  emit("update:dueDate", value === "" ? null : value);
}
</script>

<style lang="scss" scoped>
.spb-row__value :deep(.input-group-text) {
  background-color: transparent;
  border-color: #ced4da;
  color: #6c757d;
  padding: 0 0.5rem;
  font-size: 0.9rem;
}
</style>
